import React from 'react';
import { Loader2, CheckCircle, AlertCircle, Wifi, Brain, Zap, DollarSign, Clock } from 'lucide-react';

const DeepSeekProgressModal = ({ isOpen, progress, onClose, title = "DeepSeek AI 處理中" }) => {
  if (!isOpen) return null;

  const getStageIcon = (stage) => {
    switch (stage) {
      case 'connecting':
        return <Wifi className="w-5 h-5 text-blue-500 animate-pulse" />;
      case 'preparing':
        return <Brain className="w-5 h-5 text-purple-500 animate-pulse" />;
      case 'analyzing':
        return <Brain className="w-5 h-5 text-purple-500 animate-pulse" />;
      case 'generating':
        return <Zap className="w-5 h-5 text-yellow-500 animate-pulse" />;
      case 'processing':
        return <Loader2 className="w-5 h-5 text-green-500 animate-spin" />;
      case 'validating':
        return <CheckCircle className="w-5 h-5 text-blue-500 animate-pulse" />;
      case 'completing':
        return <Zap className="w-5 h-5 text-yellow-500 animate-pulse" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />;
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'connecting':
        return 'border-blue-200 bg-blue-50';
      case 'preparing':
        return 'border-purple-200 bg-purple-50';
      case 'analyzing':
        return 'border-purple-200 bg-purple-50';
      case 'generating':
        return 'border-yellow-200 bg-yellow-50';
      case 'processing':
        return 'border-green-200 bg-green-50';
      case 'validating':
        return 'border-blue-200 bg-blue-50';
      case 'completing':
        return 'border-yellow-200 bg-yellow-50';
      case 'completed':
        return 'border-green-200 bg-green-50';
      case 'error':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const getProgressPercentage = (stage) => {
    switch (stage) {
      case 'connecting':
        return 10;
      case 'preparing':
        return 20;
      case 'analyzing':
        return 35;
      case 'generating':
        return 60;
      case 'processing':
        return 75;
      case 'validating':
        return 85;
      case 'completing':
        return 95;
      case 'completed':
        return 100;
      case 'error':
        return 100;
      default:
        return 0;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {title}
          </h3>
          {progress.stage === 'completed' && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <CheckCircle className="w-6 h-6" />
            </button>
          )}
        </div>

        <div className={`border-2 rounded-lg p-4 mb-4 ${getStageColor(progress.stage)}`}>
          <div className="flex items-center space-x-3 mb-3">
            {getStageIcon(progress.stage)}
            <div className="flex-1">
              <p className="font-medium text-gray-900">
                {progress.message}
              </p>
              {progress.stage === 'error' && progress.error && (
                <p className="text-sm text-red-600 mt-1">
                  錯誤: {progress.error}
                </p>
              )}
            </div>
          </div>

          {/* Progress bar */}
          {progress.stage !== 'completed' && progress.stage !== 'error' && (
            <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${getProgressPercentage(progress.stage)}%` 
                }}
              ></div>
            </div>
          )}

          {/* Status indicators */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <span className="text-gray-600">
                時間: {progress.timeElapsed}s
              </span>
            </div>
            
            {progress.tokensUsed > 0 && (
              <div className="flex items-center space-x-2">
                <Brain className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">
                  Tokens: {progress.tokensUsed.toLocaleString()}
                </span>
              </div>
            )}

            {progress.cost && (
              <div className="flex items-center space-x-2">
                <DollarSign className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">
                  成本: ¥{progress.cost.toFixed(4)}
                </span>
              </div>
            )}

            {progress.estimatedTokens && (
              <div className="flex items-center space-x-2">
                <Zap className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">
                  預估: {progress.estimatedTokens.toLocaleString()} tokens
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end space-x-3">
          {progress.stage === 'error' && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              關閉
            </button>
          )}
          
          {progress.stage === 'completed' && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              完成
            </button>
          )}
        </div>

        {/* Additional info for completed state */}
        {progress.stage === 'completed' && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-2 text-green-700">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">
                處理成功完成！
              </span>
            </div>
            <p className="text-xs text-green-600 mt-1">
              內容已生成完成，您可以查看結果。
            </p>
          </div>
        )}

        {/* Error info */}
        {progress.stage === 'error' && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2 text-red-700">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">
                處理失敗
              </span>
            </div>
            <p className="text-xs text-red-600 mt-1">
              請檢查網絡連接或稍後再試。
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeepSeekProgressModal; 